import { useState, useEffect, useCallback, useRef } from "react";
import { toast } from "sonner";
import ForceGraph2D from "react-force-graph-2d";
import { api } from "../lib/api";
import { useTheme } from "../App";
import { translateStatus, translateRisk } from "../lib/constants";

const conflictTypeLabels = {
  cross_reference: 'Referência Cruzada',
  beneficial_owner: 'Beneficiário Efetivo',
  shareholder: 'Sócio/Acionista',
  family_relation: 'Relação Familiar',
  business_relation: 'Relação Comercial',
  ai_detected: 'Detetado por IA'
};

const ConflictsPage = () => {
  const [conflicts, setConflicts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [viewMode, setViewMode] = useState('list');
  const [graphData, setGraphData] = useState({ nodes: [], links: [] });
  const [graphStats, setGraphStats] = useState(null);
  const [loadingGraph, setLoadingGraph] = useState(false);
  const [selectedNode, setSelectedNode] = useState(null);
  const [selectedLink, setSelectedLink] = useState(null);
  const [evaluatingConflict, setEvaluatingConflict] = useState(null);
  const graphRef = useRef();
  const { theme } = useTheme();

  const fetchConflicts = async () => {
    try {
      const response = await api.get('/conflicts');
      setConflicts(response.data);
    } catch (error) {
      console.error('Error fetching conflicts:', error);
      toast.error('Erro ao carregar conflitos');
    } finally {
      setLoading(false);
    }
  };

  const fetchGraphData = async () => {
    setLoadingGraph(true);
    try {
      const response = await api.get('/conflicts/graph');
      setGraphData({ nodes: response.data.nodes, links: response.data.links });
      setGraphStats(response.data.stats);
    } catch (error) {
      console.error('Error fetching graph data:', error);
      toast.error('Erro ao carregar dados do grafo');
    } finally {
      setLoadingGraph(false);
    }
  };

  useEffect(() => { fetchConflicts(); }, []);
  useEffect(() => { if (viewMode === 'graph') fetchGraphData(); }, [viewMode]);

  const triggerScan = async () => {
    setScanning(true);
    try {
      await api.post('/conflicts/scan');
      toast.success('Análise de conflitos iniciada');
      setTimeout(() => {
        fetchConflicts();
        if (viewMode === 'graph') fetchGraphData();
      }, 3000);
    } catch (error) {
      toast.error('Erro ao iniciar análise');
    } finally {
      setScanning(false);
    }
  };

  const updateConflictStatus = async (conflictId, status, notes = null) => {
    try {
      const updateData = { status };
      if (notes) updateData.resolution_notes = notes;
      await api.put(`/conflicts/${conflictId}`, updateData);
      toast.success('Status atualizado');
      fetchConflicts();
      if (viewMode === 'graph') fetchGraphData();
    } catch (error) {
      toast.error('Erro ao atualizar status');
    }
  };

  const nodeCanvasObject = useCallback((node, ctx, globalScale) => {
    const label = node.name;
    const fontSize = 12 / globalScale;
    ctx.font = `${fontSize}px Sans-Serif`;
    ctx.beginPath();
    ctx.arc(node.x, node.y, node.size || 8, 0, 2 * Math.PI);
    ctx.fillStyle = node.hasConflict ? '#ef4444' : node.color;
    ctx.fill();
    if (node.hasConflict) {
      ctx.strokeStyle = '#991b1b';
      ctx.lineWidth = 2;
      ctx.stroke();
    }
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillStyle = theme === 'dark' ? '#e5e7eb' : '#374151';
    ctx.fillText(label, node.x, node.y + (node.size || 8) + fontSize);
  }, [theme]);

  const linkCanvasObject = useCallback((link, ctx) => {
    ctx.beginPath();
    ctx.moveTo(link.source.x, link.source.y);
    ctx.lineTo(link.target.x, link.target.y);
    ctx.strokeStyle = link.color || '#ef4444';
    ctx.lineWidth = link.width || 2;
    ctx.stroke();
  }, []);

  return (
    <div data-testid="conflicts-page" className="space-y-6 animate-fade-in">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold font-['Plus_Jakarta_Sans']">Gestão de Conflitos</h1>
          <p className="text-muted-foreground">Deteção e resolução de conflitos de interesse</p>
        </div>
        <div className="flex gap-3">
          <div className="flex rounded-lg border border-border overflow-hidden">
            <button onClick={() => setViewMode('list')} className={`px-4 py-2 text-sm font-medium transition-colors ${viewMode === 'list' ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'}`}>
              Lista
            </button>
            <button onClick={() => setViewMode('graph')} className={`px-4 py-2 text-sm font-medium transition-colors ${viewMode === 'graph' ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'}`}>
              Grafo
            </button>
          </div>
          <button onClick={triggerScan} disabled={scanning} className="px-4 py-2 rounded-lg bg-primary text-primary-foreground font-medium hover:bg-primary/90 transition-colors flex items-center gap-2 disabled:opacity-50" data-testid="scan-conflicts-btn">
            {scanning ? <><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-foreground"></div>A analisar...</> : <>
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>
              Analisar Conflitos
            </>}
          </button>
        </div>
      </div>

      {viewMode === 'graph' && graphStats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <div className="rounded-xl border bg-card p-4"><p className="text-sm text-muted-foreground">Clientes</p><p className="text-2xl font-bold text-blue-500">{graphStats.clients}</p></div>
          <div className="rounded-xl border bg-card p-4"><p className="text-sm text-muted-foreground">Fornecedores</p><p className="text-2xl font-bold text-purple-500">{graphStats.suppliers}</p></div>
          <div className="rounded-xl border bg-card p-4"><p className="text-sm text-muted-foreground">Colaboradores</p><p className="text-2xl font-bold text-emerald-500">{graphStats.employees}</p></div>
          <div className="rounded-xl border bg-card p-4"><p className="text-sm text-muted-foreground">Conflitos Ativos</p><p className="text-2xl font-bold text-amber-500">{graphStats.active_conflicts}</p></div>
          <div className="rounded-xl border bg-card p-4"><p className="text-sm text-muted-foreground">Conflitos Críticos</p><p className="text-2xl font-bold text-red-500">{graphStats.critical_conflicts}</p></div>
        </div>
      )}

      {loading || loadingGraph ? (
        <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div></div>
      ) : viewMode === 'list' ? (
        conflicts.length === 0 ? (
          <div className="text-center py-12 bg-card rounded-xl border">
            <svg className="w-12 h-12 mx-auto text-emerald-500 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
            <p className="text-xl font-semibold mb-2">Sem Conflitos Detetados</p>
            <p className="text-muted-foreground">A organização está em conformidade</p>
          </div>
        ) : (
          <div className="space-y-4">
            {conflicts.map((conflict) => (
              <div key={conflict.conflict_id} className="rounded-xl border bg-card p-6" data-testid={`conflict-card-${conflict.conflict_id}`}>
                <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${conflict.severity === 'critical' || conflict.severity === 'high' ? 'risk-badge-high' : conflict.severity === 'medium' ? 'risk-badge-medium' : 'risk-badge-low'}`}>{translateRisk(conflict.severity)}</span>
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${conflict.status === 'resolved' ? 'status-badge-approved' : conflict.status === 'dismissed' ? 'status-badge-rejected' : 'status-badge-pending'}`}>{translateStatus(conflict.status)}</span>
                    </div>
                    <h3 className="text-lg font-semibold mb-1">{conflict.entity1_name} ↔ {conflict.entity2_name}</h3>
                    <p className="text-muted-foreground mb-2">{conflict.relationship}</p>
                    <p className="text-sm"><span className="text-muted-foreground">Tipo:</span> {conflictTypeLabels[conflict.conflict_type] || conflict.conflict_type} | <span className="text-muted-foreground">Detetado:</span> {new Date(conflict.detected_at).toLocaleDateString('pt-PT')}</p>
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => setEvaluatingConflict(conflict)} className="px-3 py-1.5 rounded-lg bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400 text-sm font-medium hover:bg-blue-200 transition-colors flex items-center gap-1">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" /></svg>
                      Avaliar
                    </button>
                    {conflict.status !== 'resolved' && conflict.status !== 'dismissed' && (
                      <>
                        <button onClick={() => updateConflictStatus(conflict.conflict_id, 'resolved')} className="px-3 py-1.5 rounded-lg bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400 text-sm font-medium hover:bg-emerald-200 transition-colors">Resolver</button>
                        <button onClick={() => updateConflictStatus(conflict.conflict_id, 'dismissed')} className="px-3 py-1.5 rounded-lg bg-muted text-muted-foreground text-sm font-medium hover:bg-muted/80 transition-colors">Ignorar</button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          <div className="lg:col-span-3 rounded-xl border bg-card overflow-hidden" style={{ height: '600px' }}>
            <ForceGraph2D
              ref={graphRef}
              graphData={graphData}
              nodeCanvasObject={nodeCanvasObject}
              linkCanvasObject={linkCanvasObject}
              onNodeClick={(node) => { setSelectedNode(node); setSelectedLink(null); }}
              onLinkClick={(link) => { setSelectedLink(link); setSelectedNode(null); }}
              backgroundColor={theme === 'dark' ? '#0a0a0a' : '#ffffff'}
              linkDirectionalArrowLength={6}
              linkDirectionalArrowRelPos={1}
              cooldownTicks={100}
              onEngineStop={() => graphRef.current?.zoomToFit(400, 50)}
            />
          </div>
          <div className="space-y-4">
            {selectedNode && (
              <div className="rounded-xl border bg-card p-4">
                <h3 className="font-semibold mb-2">{selectedNode.name}</h3>
                <p className="text-sm text-muted-foreground mb-1">Tipo: {selectedNode.type}</p>
                {selectedNode.tax_id && <p className="text-sm text-muted-foreground">NIF: {selectedNode.tax_id}</p>}
                {selectedNode.department && <p className="text-sm text-muted-foreground">Depart.: {selectedNode.department}</p>}
              </div>
            )}
            {selectedLink && (
              <div className="rounded-xl border bg-card p-4">
                <h3 className="font-semibold mb-2">Conflito</h3>
                <p className="text-sm"><span className={`px-2 py-0.5 rounded text-xs ${selectedLink.severity === 'critical' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`}>{translateRisk(selectedLink.severity)}</span></p>
                <p className="text-sm text-muted-foreground mt-2">{selectedLink.relationship}</p>
              </div>
            )}
            <div className="rounded-xl border bg-card p-4">
              <h3 className="font-semibold mb-3">Legenda</h3>
              <div className="space-y-2 text-sm">
                <div className="flex items-center gap-2"><div className="w-3 h-3 rounded-full bg-blue-500"></div>Clientes</div>
                <div className="flex items-center gap-2"><div className="w-3 h-3 rounded-full bg-purple-500"></div>Fornecedores</div>
                <div className="flex items-center gap-2"><div className="w-3 h-3 rounded-full bg-emerald-500"></div>Colaboradores</div>
                <div className="flex items-center gap-2"><div className="w-3 h-3 rounded-full bg-red-500"></div>Com Conflito</div>
              </div>
            </div>
          </div>
        </div>
      )}

      {evaluatingConflict && (
        <ConflictEvaluationModal
          conflict={evaluatingConflict}
          onClose={() => setEvaluatingConflict(null)}
          onUpdate={(status, notes) => { updateConflictStatus(evaluatingConflict.conflict_id, status, notes); setEvaluatingConflict(null); }}
        />
      )}
    </div>
  );
};

// Conflict Evaluation Modal
const ConflictEvaluationModal = ({ conflict, onClose, onUpdate }) => {
  const [evaluating, setEvaluating] = useState(false);
  const [aiAnalysis, setAiAnalysis] = useState(null);
  const [notes, setNotes] = useState('');

  const runAiAnalysis = async () => {
    setEvaluating(true);
    try {
      const response = await api.post(`/conflicts/${conflict.conflict_id}/evaluate`);
      setAiAnalysis(response.data);
    } catch (error) {
      toast.error('Erro ao avaliar conflito');
    } finally {
      setEvaluating(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-card rounded-xl border border-border w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="p-6 border-b border-border flex items-center justify-between">
          <h2 className="text-xl font-semibold">Avaliação de Conflito</h2>
          <button onClick={onClose} className="p-2 hover:bg-muted rounded-lg"><svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg></button>
        </div>
        <div className="p-6 space-y-4">
          <div className="rounded-lg bg-muted/50 p-4">
            <h3 className="font-semibold mb-2">{conflict.entity1_name} ↔ {conflict.entity2_name}</h3>
            <p className="text-sm text-muted-foreground">{conflict.relationship}</p>
            <div className="flex gap-2 mt-2">
              <span className={`px-2 py-1 rounded text-xs ${conflict.severity === 'critical' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`}>{translateRisk(conflict.severity)}</span>
              <span className="px-2 py-1 rounded text-xs bg-muted">{translateStatus(conflict.status)}</span>
            </div>
          </div>

          <button onClick={runAiAnalysis} disabled={evaluating} className="w-full px-4 py-2 rounded-lg bg-blue-600 text-white font-medium hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2">
            {evaluating ? <><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>A avaliar com IA...</> : <>
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" /></svg>
              Avaliar com IA
            </>}
          </button>

          {aiAnalysis && (
            <div className="rounded-lg border p-4 space-y-3">
              <h4 className="font-semibold">Análise IA</h4>
              <p className="text-sm">{aiAnalysis.analysis || aiAnalysis.recommendation}</p>
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground">Risco:</span>
                <span className={`px-2 py-1 rounded text-xs ${aiAnalysis.risk_level === 'high' ? 'bg-red-100 text-red-700' : aiAnalysis.risk_level === 'medium' ? 'bg-amber-100 text-amber-700' : 'bg-green-100 text-green-700'}`}>{translateRisk(aiAnalysis.risk_level)}</span>
              </div>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium mb-1">Notas de Resolução</label>
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} className="w-full px-3 py-2 rounded-lg border border-input bg-background focus:outline-none focus:ring-2 focus:ring-ring" placeholder="Adicionar notas..." />
          </div>

          <div className="flex gap-3 pt-4">
            <button onClick={() => onUpdate('dismissed', notes)} className="flex-1 px-4 py-2 rounded-lg border border-border hover:bg-muted transition-colors">Ignorar</button>
            <button onClick={() => onUpdate('resolved', notes)} className="flex-1 px-4 py-2 rounded-lg bg-emerald-600 text-white font-medium hover:bg-emerald-700 transition-colors">Resolver</button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConflictsPage;
